import InfoModal from '@/components/molecules/InfoModal';
import ECCreationForm from '@/components/organisms/ECCreationForm';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { expenseRevenueService } from '@/services/expense-category.service';
import toast from 'react-hot-toast';
import MobileAppBar from '@/components/molecules/MobileAppBar';
import { budgetService } from '@/services/budget.service';
import { ExpenseCategoryFormValues } from '@/services/types/expense-category.types';
import { useNavigationGuard } from '@/hooks/useNavigationGuard';
import profileDiscardIcon from '@/assets/icons/profile-discard.svg';

const ECAddPage = () => {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [maxExpenseValue, setMaxExpenseValue] = useState(0);
  const [isFormDirty, setIsFormDirty] = useState(false);

  const navigate = useNavigate();
  const { budgetId } = useParams();
  const location = useLocation();
  const defaultExpenseCategory = location.state?.defaultExpenseCategory as any | undefined;

  const { isDiscardModalOpen, handleCancel, handleDiscardChanges, handleDiscardModalClose, allowNavigation } =
    useNavigationGuard({
      isDirty: isFormDirty,
      fallbackPath: `/budgets/${budgetId}/expense-categories`,
      // This page is always pushed from the EC list page above, so cancelling
      // should pop back to it rather than replace (which would create a
      // duplicate consecutive history entry and break the back button).
      backOnCancel: true
    });

  console.log('Default Expense Category', defaultExpenseCategory);

  useEffect(() => {
    fetchBudgetDetails();
  }, [budgetId]);

  const fetchBudgetDetails = async () => {
    if (!budgetId) {
      setError('Budget ID not found');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const { data } = await budgetService.getBudget(budgetId);

      // For expense categories, the max expense value should be the remaining BMEV
      setMaxExpenseValue(data.bmev - (data?.bmev_used || 0));
    } catch (err) {
      console.error('Error fetching budget:', err);
      setError('Failed to load budget details');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (data: ExpenseCategoryFormValues) => {
    console.log('Submit', data);
    try {
      if (!budgetId) {
        throw new Error('BudgetId is required');
      }

      const response = await expenseRevenueService.createExpenseCategory(budgetId, {
        name: data.name,
        description: data.description,
        ecmev: Number(data.ecmev),
        ecmev_alert_threshold: Number(data.ecmev_alert_threshold)
      });

      console.log('Response', response);
      toast.success('Expense category created successfully');
      allowNavigation();
      // This page was pushed from the EC list page, so pop back to it instead
      // of replacing — replacing to the same URL as the previous history
      // entry would create a duplicate entry and break the back button.
      navigate(-1);
    } catch (error) {
      console.error('Error creating expense category:', error);
      toast.error('Failed to create expense category');
    }
  };

  return (
    <>
      <MobileAppBar title="Expense Category Creation" />

      <div className="bg-[#FBFBFF] p-4 flex flex-col flex-1 md:bg-white rounded-[16px] border">
        <h2 className="text-center w-[80%] mx-auto md:w-full font-semibold mb-5 md:text-left md:text-[24px] md:mb-6">
          Create your Expense Category
        </h2>

        {/* FORM */}
        <ECCreationForm
          onSubmit={handleSubmit}
          maxExpenseValue={defaultExpenseCategory?.ecmev || maxExpenseValue}
          onFormStateChange={({ isDirty }) => setIsFormDirty(isDirty)}
          onCancel={handleCancel}
        />
      </div>

      {isDiscardModalOpen && (
        <InfoModal
          isOpen={isDiscardModalOpen}
          variant="confirm"
          title="Are you sure you want to discard changes?"
          message="Unsaved changes will not be saved into your expense category"
          buttonText="Discard"
          onButtonClick={handleDiscardChanges}
          icon={profileDiscardIcon}
          onClose={handleDiscardModalClose}
        />
      )}

      {showSuccessModal && (
        <InfoModal
          isOpen={showSuccessModal}
          variant="success"
          title="Expense Category Created"
          message={<p>You have successfully created an Expense Category!</p>}
          buttonText="Go to Expense Categories"
          onButtonClick={() => {
            setShowSuccessModal(false);
            navigate(-1);
          }}
        />
      )}
    </>
  );
};

export default ECAddPage;
